/**
 * Voice turn runner for spoken rapid-fire answers (spec 02). Owns the mic loop for ONE question;
 * advancing never awaits transcription — see ADR-0002 §1. `levelSource` is a test-only injection
 * point: jsdom has no getUserMedia/MediaRecorder/AudioContext, so it bypasses the real media stack
 * and drives the SAME `nextSilenceState` loop the production analyser feeds (plan-02 Task 3/4).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { VOICE_CAPTURE } from '../../config/constants';
import {
  initialSilenceState,
  nextSilenceState,
  frameLevel,
  type SilenceState,
} from './voiceActivity';
import { isBlobLongEnough } from '../../utils/audioCapture';
import { MicButtonVisual } from './MicButtonVisual';

/** Reuses the MediaRecorder timeslice — same underlying cadence as a fresh mic reading. */
const TICK_MS = VOICE_CAPTURE.TIMESLICE_MS;
const RING_RADIUS = 17;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
/** Wraps MicButtonVisual's fixed 96px button with its old visible gap; ring geometry is untouched. */
const RING_WRAPPER_SIZE_CLASS = 'w-36 h-36';
/** One scalar per tick (silence detection), so all 7 bars move together rather than a new visual. */
const AUDIO_LEVEL_BAR_COUNT = 7;
/** Test-mode placeholder blob, sized past MIN_BLOB_BYTES so the length guard behaves normally. */
const TEST_PLACEHOLDER_BLOB = () => new Blob([new Uint8Array(VOICE_CAPTURE.MIN_BLOB_BYTES)]);

export interface RapidFireVoiceQuestion {
  id: string;
  type: string;
  text: string;
}

export interface RapidFireTranscriptResult {
  questionId: string;
  transcript: string | null;
  /** True only when the clip was too short to hold speech (`isBlobLongEnough` never tried) — vs.
   *  `transcript === null` from a failed/incomplete call. See ADR-0002 §1. */
  noAudioCaptured?: boolean;
}

interface RapidFireVoiceRunnerProps {
  question: RapidFireVoiceQuestion;
  sessionId: number;
  onAdvance: () => void;
  /** Flat `{ questionId, transcript }`, never a write into shared response state. */
  onTranscript?: (result: RapidFireTranscriptResult) => void;
  onUndo?: (questionId: string) => void;
  /** Injected so production and tests share one call shape. */
  transcribe: (blob: Blob) => Promise<string>;
  /** Test-only: replaces the real analyser reading with a fixed/controlled source. */
  levelSource?: () => number;
}

type Mode = 'starting' | 'listening' | 'typed-fallback';

const SILENCE_OPTS = {
  threshold: VOICE_CAPTURE.SILENCE_LEVEL_THRESHOLD,
  advanceAfterMs: VOICE_CAPTURE.SILENCE_ADVANCE_MS,
};

export const RapidFireVoiceRunner: React.FC<RapidFireVoiceRunnerProps> = ({
  question,
  onAdvance,
  onTranscript,
  onUndo,
  transcribe,
  levelSource,
}) => {
  const [mode, setMode] = useState<Mode>('starting');
  const [silentMs, setSilentMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [typedValue, setTypedValue] = useState('');
  const [showUndo, setShowUndo] = useState(true);
  const [advanced, setAdvanced] = useState(false);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-question by construction: SurveySession renders this with key={currentQuestion.id}, so a
  // new question REMOUNTS and this ref re-initialises with hasSpoken=false. That matters — carrying
  // hasSpoken across questions would let question 2 advance on silence before the respondent had
  // said anything. If that key is ever removed, reset this ref per question instead.
  const silenceStateRef = useRef<SilenceState>(initialSilenceState());
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const freqBufferRef = useRef<Uint8Array | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  // Always call the LATEST callbacks. The tick loop and the transcription promise both outlive
  // any single render, and a transcript resolving after the respondent has moved on (or this
  // component has unmounted) must still deliver correctly rather than close over a stale prop.
  const onAdvanceRef = useRef(onAdvance);
  const onTranscriptRef = useRef(onTranscript);
  const transcribeRef = useRef(transcribe);
  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  }, [onAdvance]);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);
  useEffect(() => {
    transcribeRef.current = transcribe;
  }, [transcribe]);

  const stopTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    audioContextRef.current = null;
    analyserRef.current = null;
  }, []);

  /** Fires exactly once, when sustained silence crosses SILENCE_ADVANCE_MS (or the typed fallback
   * is submitted). Never awaits transcription — that runs on its own and is delivered later. */
  const fireAdvance = useCallback(
    (blob: Blob) => {
      stopTick();
      setAdvanced(true);
      onAdvanceRef.current();

      const deliver = (transcript: string | null, noAudioCaptured = false) => {
        onTranscriptRef.current?.({ questionId: question.id, transcript, noAudioCaptured });
      };
      if (!isBlobLongEnough(blob.size, VOICE_CAPTURE.MIN_BLOB_BYTES)) {
        // Too short to be speech — genuine silence, not a transcription failure.
        deliver(null, true);
        return;
      }
      transcribeRef
        .current(blob)
        .then((text) => deliver(text))
        // The clip WAS long enough to be speech; transcription itself failed or never returned.
        // `noAudioCaptured` stays false so the caller keeps whatever pending marker it wrote on
        // advance rather than overwriting it with an empty answer.
        .catch(() => deliver(null));
    },
    [question.id, stopTick],
  );

  // The silence loop itself — identical whether the level comes from a real analyser or an
  // injected test source, so the SAME detection code path runs in both.
  const runTick = useCallback(
    (readLevel: () => number, readBlob: () => Blob) => {
      stopTick();
      tickRef.current = setInterval(() => {
        const currentLevel = readLevel();
        const next = nextSilenceState(silenceStateRef.current, currentLevel, TICK_MS, SILENCE_OPTS);
        silenceStateRef.current = next;
        setSilentMs(next.silentMs);
        setLevel(currentLevel);
        if (next.shouldAdvance) fireAdvance(readBlob());
      }, TICK_MS);
    },
    [fireAdvance, stopTick],
  );

  // --- mount: start capture (test-driven or real), once per question -------------------------
  useEffect(() => {
    let cancelled = false;

    if (levelSource) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect -- this branch only ever runs
         in tests (levelSource bypasses the real media stack entirely); there is no readiness
         wait to synchronize with here the way the real getUserMedia path has one. */
      setMode('listening');
      runTick(levelSource, TEST_PLACEHOLDER_BLOB);
      return () => {
        stopTick();
      };
    }

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        const mimeType = MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : MediaRecorder.isTypeSupported('audio/wav')
              ? 'audio/wav'
              : '';
        if (!mimeType) throw new Error('No supported audio format found');

        const AudioContextCtor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const audioContext = new AudioContextCtor();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.1;
        source.connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
        recorderRef.current = recorder;
        chunksRef.current = [];

        const readLevel = () => {
          const a = analyserRef.current;
          if (!a) return 0;
          if (!freqBufferRef.current || freqBufferRef.current.length !== a.frequencyBinCount) {
            freqBufferRef.current = new Uint8Array(a.frequencyBinCount);
          }
          a.getByteFrequencyData(freqBufferRef.current);
          return frameLevel(freqBufferRef.current);
        };
        const readBlob = () => new Blob(chunksRef.current, { type: mimeType });

        // First real chunk = proof capture is actually live. Only then does the silence clock
        // start — before that a slow device could silently drop the respondent's first words.
        recorder.ondataavailable = (event) => {
          if (event.data.size === 0) return;
          chunksRef.current.push(event.data);
          if (readinessTimerRef.current) {
            clearTimeout(readinessTimerRef.current);
            readinessTimerRef.current = null;
            if (!cancelled) {
              setMode('listening');
              runTick(readLevel, readBlob);
            }
          }
        };

        // If no chunk ever arrives (slow/blocked device), fall back to typed input rather than
        // leave the respondent on a dead recording — "never a dead end" (spec-02 edge cases).
        readinessTimerRef.current = setTimeout(() => {
          readinessTimerRef.current = null;
          try {
            if (recorder.state === 'recording') recorder.stop();
          } catch {
            /* noop */
          }
          cleanupMedia();
          if (!cancelled) setMode('typed-fallback');
        }, VOICE_CAPTURE.READINESS_TIMEOUT_MS);

        recorder.start(VOICE_CAPTURE.TIMESLICE_MS);
      } catch {
        // Mic permission denied, or no supported audio format — same outcome either way.
        if (!cancelled) setMode('typed-fallback');
      }
    })();

    return () => {
      cancelled = true;
      stopTick();
      if (readinessTimerRef.current) {
        clearTimeout(readinessTimerRef.current);
        readinessTimerRef.current = null;
      }
      try {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      } catch {
        /* noop */
      }
      cleanupMedia();
    };
    // Runs once per mounted instance — a new question is a new instance (SurveySession keys the
    // runner on question id), so this deliberately does not react to prop identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- undo window: available from the moment this question is reached ------------------------
  useEffect(() => {
    const timer = setTimeout(() => setShowUndo(false), VOICE_CAPTURE.UNDO_WINDOW_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleTypedSubmit = useCallback(() => {
    if (!typedValue.trim()) return;
    stopTick();
    setAdvanced(true);
    onAdvanceRef.current();
    // Typed text needs no transcription step — it already IS the answer.
    onTranscriptRef.current?.({ questionId: question.id, transcript: typedValue });
  }, [question.id, stopTick, typedValue]);

  const silenceFraction = Math.max(0, Math.min(1, silentMs / VOICE_CAPTURE.SILENCE_ADVANCE_MS));
  // All 7 bars move together with the one scalar this loop already reads each tick for silence
  // detection — see AUDIO_LEVEL_BAR_COUNT's docblock.
  const audioLevels = new Array(AUDIO_LEVEL_BAR_COUNT).fill(level);

  return (
    <div className="space-y-6 text-center">
      {/* No question text here — SurveySession already renders it via QuestionText above the
          input; repeating it duplicated every heading on screen. */}
      {mode !== 'typed-fallback' && !advanced && (
        <div className="flex flex-col items-center space-y-3">
          <div className={`relative ${RING_WRAPPER_SIZE_CLASS} flex items-center justify-center`}>
            <svg
              className={`absolute inset-0 ${RING_WRAPPER_SIZE_CLASS} -rotate-90`}
              viewBox="0 0 40 40"
              aria-hidden="true"
            >
              <circle
                cx="20"
                cy="20"
                r={RING_RADIUS}
                className="stroke-gray-200"
                strokeWidth={3}
                fill="none"
              />
              <circle
                cx="20"
                cy="20"
                r={RING_RADIUS}
                className="stroke-blue-500"
                strokeWidth={3}
                fill="none"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - silenceFraction)}
                style={{ transition: `stroke-dashoffset ${TICK_MS}ms linear` }}
              />
            </svg>
            <MicButtonVisual
              isRecording={mode === 'listening'}
              isTranscribing={false}
              audioLevels={audioLevels}
            />
          </div>
          <p className="text-sm text-gray-500">
            {mode === 'starting' ? 'Getting your microphone ready…' : 'Listening…'}
          </p>
        </div>
      )}

      {mode === 'typed-fallback' && !advanced && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            We couldn&apos;t start your microphone. Type your answer instead.
          </p>
          <textarea
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
            autoFocus
          />
          <button
            type="button"
            onClick={handleTypedSubmit}
            disabled={!typedValue.trim()}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40"
          >
            Submit answer
          </button>
        </div>
      )}

      {showUndo && onUndo && (
        <button
          type="button"
          onClick={() => onUndo(question.id)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
          <span>Undo</span>
        </button>
      )}
    </div>
  );
};
